import React from "react";
import { Plus, X } from "lucide-react";
import { Button } from "./ui/button";
import TimePicker from "./TimePicker";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function TimeSlotsEditor({ value = {}, onChange }) {
  const set = (next) => onChange(next);

  const addSlot = (day) => {
    const cur = value[day] || [];
    // Adding an identical window twice is meaningless and would also collide
    // with the content-based React keys below, so nudge the new slot along.
    let start = "09:00", end = "17:00";
    if (cur.some(s => s.start === start && s.end === end)) {
      const used = new Set(cur.map(s => `${s.start}-${s.end}`));
      let hour = 8;
      while (hour < 22 && used.has(`${String(hour).padStart(2, "0")}:00-${String(hour + 1).padStart(2, "0")}:00`)) hour += 1;
      start = `${String(hour).padStart(2, "0")}:00`;
      end = `${String(Math.min(hour + 1, 23)).padStart(2, "0")}:00`;
    }
    set({ ...value, [day]: [...cur, { start, end }] });
  };
  const removeSlot = (day, i) => {
    const cur = value[day] || [];
    set({ ...value, [day]: cur.filter((_, j) => j !== i) });
  };
  const updateSlot = (day, i, key, v) => {
    const cur = value[day] || [];
    set({ ...value, [day]: cur.map((s, j) => j === i ? { ...s, [key]: v } : s) });
  };
  const copyMon = () => {
    const mon = value.mon || [];
    const next = { ...value };
    DAYS.slice(1, 5).forEach(d => { next[d] = JSON.parse(JSON.stringify(mon)); });
    set(next);
  };

  return (
    <div className="border border-border bg-card p-4" data-testid="time-slots-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Weekly availability</div>
        <button type="button" onClick={copyMon} className="text-xs text-primary hover:underline" data-testid="copy-mon-btn">
          Copy Monday → Tue–Fri
        </button>
      </div>
      <div className="space-y-2">
        {DAYS.map(day => {
          const slots = value[day] || [];
          return (
            <div key={day} className="flex items-center gap-2" data-testid={`slot-row-${day}`}>
              <div className="w-12 text-xs uppercase tracking-widest font-bold">{day}</div>
              <div className="flex-1 flex flex-wrap gap-2">
                {slots.map((s, i) => (
                  /* Content-based key, NOT the array index: removing slot 0
                     with an index key makes React reuse the wrong TimePicker
                     instance and the remaining rows show stale times. */
                  <div key={`${day}-${s.start}-${s.end}`} className="flex items-center gap-1 border border-border bg-background px-1.5 py-0.5">
                    <TimePicker value={s.start} onChange={(v) => updateSlot(day, i, "start", v)}
                                testId={`slot-${day}-${i}-start`} />
                    <span className="text-xs text-muted-foreground">–</span>
                    <TimePicker value={s.end} onChange={(v) => updateSlot(day, i, "end", v)}
                                testId={`slot-${day}-${i}-end`} />
                    <button type="button" onClick={() => removeSlot(day, i)} className="text-muted-foreground hover:text-destructive" data-testid={`slot-${day}-${i}-remove`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => addSlot(day)} className="h-7" data-testid={`slot-${day}-add`}>
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        Time-slots define when learners can book you. Leave a day empty to mark it closed.
      </p>
    </div>
  );
}
